import {
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  FilePdfOutlined,
  FileTextOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { Button, Space, Table, Tag, Typography } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  createQuestionnaireAsAdmin,
  deleteQuestionnaireAsAdmin,
  exportToPDF,
  fetchQuestionnairesAsAdmin,
  updateQuestionnaireAsAdmin,
} from '@/apis';
import { QuestionnaireModal } from '@/components/Admin/Modal';
import PageHeader from '@/components/Admin/PageHeader';
import StateRenderer from '@/components/Admin/StateRenderer';
import TableContainer from '@/components/Admin/TableContainer';
import { MessageType, Path } from '@/enums';
import { useApiRequest, useAppFeedback } from '@/hooks';
import {
  CreateQuestionnaireAsAdminRequest,
  UpdateQuestionnaireAsAdminRequest,
} from '@/types/apis/admin';
import { AdminQuestionnaire } from '@/types/components';
import { formatDateTime } from '@/utils/dateFormatter';

const { Text } = Typography;

const pageSize = 10;

type ResultFilter = 'all' | 'hasResultPage';

const AdminQuestionnairesPage = () => {
  const [questionnaires, setQuestionnaires] = useState<AdminQuestionnaire[]>(
    [],
  );
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [isQuestionnaireModalOpen, setQuestionnaireModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'add' | 'edit'>('add');
  const [selectedQuestionnaire, setSelectedQuestionnaire] =
    useState<AdminQuestionnaire | null>(null);
  const [exportingId, setExportingId] = useState<string | null>(null);
  const [resultFilter, setResultFilter] = useState<ResultFilter>('all');

  const { showMessage, showConfirm } = useAppFeedback();
  const navigate = useNavigate();

  // 根據篩選器計算 API 參數
  const getFilterParams = () => {
    if (resultFilter === 'hasResultPage') {
      return { hasResult: undefined, isResultComplete: true };
    }
    return { hasResult: undefined, isResultComplete: undefined };
  };

  // Use useApiRequest for questionnaire list fetching
  const {
    loading,
    error: fetchError,
    execute: loadQuestionnaires,
  } = useApiRequest(
    async () => {
      const { hasResult, isResultComplete } = getFilterParams();
      const response = await fetchQuestionnairesAsAdmin(
        page,
        pageSize,
        hasResult,
        isResultComplete,
      );
      setQuestionnaires(response.data.items);
      setTotal(response.data.total);
    },
    {
      requestKey: `admin-questionnaires-${page}-${resultFilter}`,
      onError: (error) => {
        const errorMessage = '載入問卷失敗，請稍後再試';
        showMessage(errorMessage, MessageType.Error);
        console.error('Error loading questionnaires:', error);
      },
    },
  );

  const error = fetchError ? '載入問卷失敗，請稍後再試' : null;

  // 當篩選器改變時，重置到第一頁
  const handleFilterChange = (value: ResultFilter) => {
    setResultFilter(value);
    setPage(1);
  };

  useEffect(() => {
    loadQuestionnaires();
  }, [page, resultFilter]);

  const handleSaveQuestionnaire = async (
    data:
      | AdminQuestionnaire
      | Omit<AdminQuestionnaire, 'id' | 'createdAt' | 'updatedAt'>,
  ) => {
    try {
      if (modalMode === 'edit' && 'id' in data) {
        // Edit mode
        const updateData: UpdateQuestionnaireAsAdminRequest = {
          currentQuestionId: data.currentQuestionId || '',
          ...(data.industry?.id ? { industryId: data.industry.id } : {}),
        };
        await updateQuestionnaireAsAdmin(data.id, updateData);
        showMessage('問卷更新成功', MessageType.Success);
      } else {
        // Add mode
        const createData: CreateQuestionnaireAsAdminRequest = {
          userId: data.user?.id || '',
          currentQuestionId: data.currentQuestionId || '',
          ...(data.industry?.id ? { industryId: data.industry.id } : {}),
        };
        await createQuestionnaireAsAdmin(createData);
        showMessage('問卷新增成功', MessageType.Success);
      }
      setQuestionnaireModalOpen(false);
      setSelectedQuestionnaire(null);
      loadQuestionnaires();
    } catch (error) {
      const errorMessage =
        modalMode === 'edit'
          ? '問卷更新失敗，請稍後再試'
          : '問卷新增失敗，請稍後再試';
      showMessage(errorMessage, MessageType.Error);
      console.error(
        `Error ${modalMode === 'edit' ? 'updating' : 'creating'} questionnaire:`,
        error,
      );
    }
  };

  const handleAddQuestionnaire = () => {
    setModalMode('add');
    setSelectedQuestionnaire(null);
    setQuestionnaireModalOpen(true);
  };

  const handleEditQuestionnaire = (questionnaire: AdminQuestionnaire) => {
    setModalMode('edit');
    setSelectedQuestionnaire(questionnaire);
    setQuestionnaireModalOpen(true);
  };

  const handleDelete = async (id: string) => {
    showConfirm(
      '確認刪除',
      '確定要刪除這份問卷嗎？此操作無法復原。',
      async () => {
        try {
          await deleteQuestionnaireAsAdmin(id);
          showMessage('問卷刪除成功', MessageType.Success);
          loadQuestionnaires();
        } catch (error) {
          showMessage('問卷刪除失敗，請稍後再試', MessageType.Error);
          console.error('Error deleting questionnaire:', error);
        }
      },
      undefined,
      '確定刪除',
      '取消',
      'danger',
    );
  };

  const handleViewResult = (questionnaireId: string) => {
    navigate(Path.Result.replace(':id', questionnaireId));
  };

  const handleExportPDF = async (questionnaireId: string, userName: string) => {
    setExportingId(questionnaireId);
    try {
      const resultUrl = `${window.location.origin}${Path.Result.replace(':id', questionnaireId)}`;
      const filename = `SDG_Result_${userName}_${new Date().toISOString().split('T')[0]}.pdf`;

      const blob = await exportToPDF(resultUrl, filename);

      // Create a download link and trigger download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      showMessage('PDF 匯出成功', MessageType.Success);
    } catch (err) {
      const errorMessage = 'PDF 匯出失敗，請稍後再試';
      showMessage(errorMessage, MessageType.Error);
      console.error('Error exporting PDF:', err);
    } finally {
      setExportingId(null);
    }
  };

  const questionnaireColumns: ColumnsType<AdminQuestionnaire> = [
    {
      title: '用戶',
      dataIndex: 'user',
      key: 'user',
      render: (user: AdminQuestionnaire['user']) => (
        <div>
          <Text strong style={{ fontSize: '14px' }}>
            {user.name}
          </Text>
          <br />
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {user.email}
          </Text>
        </div>
      ),
    },
    {
      title: '問卷 ID',
      dataIndex: 'id',
      key: 'id',
      render: (id: string) => (
        <Text
          type="secondary"
          style={{ fontSize: '12px', fontFamily: 'monospace' }}
        >
          {id}
        </Text>
      ),
    },
    {
      title: '填寫進度',
      key: 'progress',
      width: 120,
      render: (_, questionnaire) => {
        const { answeredCount = 0, totalQuestions = 0 } = questionnaire;
        const hasProgress = totalQuestions > 0;
        const percentage = hasProgress
          ? Math.round((answeredCount / totalQuestions) * 100)
          : 0;

        return (
          <div>
            <Text style={{ fontSize: '14px', fontWeight: 500 }}>
              {hasProgress ? `${answeredCount}/${totalQuestions}` : 'N/A'}
            </Text>
            {hasProgress && (
              <>
                <br />
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  ({percentage}%)
                </Text>
              </>
            )}
          </div>
        );
      },
    },
    {
      title: '結果頁',
      key: 'resultStatus',
      width: 100,
      render: (_, questionnaire) =>
        questionnaire.isResultComplete ? (
          <Tag color="green">有</Tag>
        ) : (
          <Tag color="default">無</Tag>
        ),
      filterDropdown: ({ confirm }) => (
        <div style={{ padding: 8 }}>
          <Space direction="vertical">
            <div>
              <Text strong>篩選：</Text>
            </div>
            {[
              { value: 'all', label: '全部' },
              { value: 'hasResultPage', label: '有結果頁' },
            ].map((option) => (
              <div key={option.value}>
                <Button
                  size="small"
                  type={resultFilter === option.value ? 'primary' : 'default'}
                  onClick={() => {
                    handleFilterChange(option.value as ResultFilter);
                    confirm();
                  }}
                  style={{ width: '100%', textAlign: 'left' }}
                >
                  {option.label}
                </Button>
              </div>
            ))}
          </Space>
        </div>
      ),
      filtered: resultFilter !== 'all',
    },
    {
      title: '當前問題ID',
      dataIndex: 'currentQuestionId',
      key: 'currentQuestionId',
      width: 200,
      render: (currentQuestionId: string) => (
        <Text style={{ fontSize: '12px', fontFamily: 'monospace' }}>
          {currentQuestionId || 'N/A'}
        </Text>
      ),
    },
    {
      title: '行業',
      dataIndex: 'industry',
      key: 'industry',
      width: 120,
      render: (industry: AdminQuestionnaire['industry']) => (
        <Text type="secondary" style={{ fontSize: '14px' }}>
          {industry?.name || '未設定'}
        </Text>
      ),
    },
    {
      title: '建立時間',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 160,
      render: (createdAt: string) => (
        <Text type="secondary" style={{ fontSize: '12px' }}>
          {formatDateTime(new Date(createdAt))}
        </Text>
      ),
    },
    {
      title: '更新時間',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: 160,
      render: (updatedAt: string) => (
        <Text type="secondary" style={{ fontSize: '12px' }}>
          {formatDateTime(new Date(updatedAt))}
        </Text>
      ),
    },
    {
      title: '操作',
      key: 'actions',
      width: 200,
      render: (_, questionnaire) => (
        <Space size="small">
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => handleEditQuestionnaire(questionnaire)}
            title="編輯問卷"
            size="small"
          />
          <Button
            type="text"
            icon={<EyeOutlined />}
            onClick={() => handleViewResult(questionnaire.id)}
            disabled={!questionnaire.isResultComplete}
            title={
              questionnaire.isResultComplete ? '查看結果' : '結果尚未完整生成'
            }
            size="small"
          />
          <Button
            type="text"
            icon={<FilePdfOutlined />}
            onClick={() =>
              handleExportPDF(questionnaire.id, questionnaire.user.name)
            }
            loading={exportingId === questionnaire.id}
            disabled={!questionnaire.isResultComplete}
            title={
              questionnaire.isResultComplete ? '匯出 PDF' : '結果尚未完整生成'
            }
            size="small"
          />
          <Button
            type="text"
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(questionnaire.id)}
            title="刪除問卷"
            danger
            size="small"
          />
        </Space>
      ),
    },
  ];

  return (
    <>
      <PageHeader title="問卷管理" description="建立、發布和管理您的問卷。" />

      <TableContainer
        title="問卷列表"
        actions={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAddQuestionnaire}
          >
            新增問卷
          </Button>
        }
      >
        <StateRenderer
          loading={loading}
          error={error}
          empty={questionnaires.length === 0}
          emptyMessage={
            resultFilter === 'all'
              ? '尚未建立任何問卷'
              : '沒有符合篩選條件的問卷'
          }
          emptyIcon={<FileTextOutlined />}
          onRetry={loadQuestionnaires}
        >
          <Table
            scroll={{ x: 'max-content' }}
            columns={questionnaireColumns}
            dataSource={questionnaires}
            rowKey="id"
            pagination={{
              current: page,
              pageSize: pageSize,
              total: total,
              onChange: setPage,
              showSizeChanger: false,
              showQuickJumper: true,
              showTotal: (total, range) =>
                `第 ${range[0]}-${range[1]} 筆，共 ${total} 筆`,
            }}
            size="small"
            loading={loading}
          />
        </StateRenderer>
      </TableContainer>

      <QuestionnaireModal
        isOpen={isQuestionnaireModalOpen}
        questionnaire={selectedQuestionnaire}
        mode={modalMode}
        onClose={() => {
          setQuestionnaireModalOpen(false);
          setSelectedQuestionnaire(null);
        }}
        onSave={handleSaveQuestionnaire}
      />
    </>
  );
};

export default AdminQuestionnairesPage;
